import { useState, useRef, useEffect } from 'react';
import { downloadRecording } from 'API/MedicalCommittees';
import { downloadFile, getFileTypeFromBase64 } from 'Utils';

interface UseRecordingPlayerProps {
  documentNumber?: string;
  documentName?: string;
  autoPlay?: boolean;
}

export const useRecordingPlayer = ({
  documentNumber,
  documentName,
  autoPlay = false,
}: UseRecordingPlayerProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioSrc, setAudioSrc] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (!audioRef.current || !audioSrc) return;

    if (isPlaying) {
      audioRef.current.play().catch((error) => {
        console.error('Error playing audio:', error);
        setIsPlaying(false);
      });
    } else {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, [isPlaying, audioSrc]);

  const loadRecording = async () => {
    if (!documentNumber) return null;

    try {
      setIsLoading(true);
      const audioData = await downloadRecording(documentNumber);

      if (audioData?.fileContentBase64) {
        const audioSource = `data:audio/mp3;base64,${audioData.fileContentBase64}`;
        setAudioSrc(audioSource);
        setIsLoading(false);
        return audioSource;
      } else {
        console.error('No audio content received');
        setIsLoading(false);
        return null;
      }
    } catch (error) {
      console.error('Failed to load recording:', error);
      setIsLoading(false);
      return null;
    }
  };

  const handlePlay = async () => {
    if (isPlaying) {
      setIsPlaying(false);
      return;
    }

    if (audioSrc) {
      setIsPlaying(true);
      return;
    }

    const loadedAudioSrc = await loadRecording();
    if (loadedAudioSrc) {
      setIsPlaying(true);
    }
  };

  const handleDownload = async () => {
    if (!documentNumber) return;

    try {
      const audioData = await downloadRecording(documentNumber);

      if (audioData?.fileContentBase64) {
        const fileType = getFileTypeFromBase64(
          audioData.fileContentBase64,
          audioData.documentName
        );
        downloadFile(
          audioData.fileContentBase64,
          fileType,
          documentName || audioData.documentName || 'recording.mp3',
          false
        );
      }
    } catch (error) {
      console.error('Error downloading recording:', error);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  const resetPlayer = () => {
    setIsPlaying(false);
    setAudioSrc(null);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };

  return {
    isPlaying,
    audioSrc,
    isLoading,
    audioRef,
    handlePlay,
    handleDownload,
    handleAudioEnded,
    resetPlayer,
    setIsPlaying,
  };
};
